/**
 * 시간대 계산. 라이브러리 없이 Intl 만 쓴다.
 *
 * 저장은 UTC, 화면은 고른 시간대(기본 KST)로 보여준다 (MVP §10-1 "한국 시간으로 표시").
 */

export const KST = 'Asia/Seoul';

const partsFormatter = new Map();

const zonedParts = (date, tz) => {
  let f = partsFormatter.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
    partsFormatter.set(tz, f);
  }
  const p = Object.fromEntries(f.formatToParts(date).map((x) => [x.type, x.value]));
  return {
    y: Number(p.year),
    m: Number(p.month),
    d: Number(p.day),
    h: Number(p.hour),
    min: Number(p.minute),
  };
};

/** tz 의 UTC 오프셋(분). KST → 540 */
export const tzOffsetMinutes = (date, tz) => {
  const p = zonedParts(date, tz);
  const asUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
  // 벽시계는 분까지만 보므로 비교할 시각도 분 단위로 자른다 — 초가 30 이상이면 1분 어긋나 UTC+6:59 가 됐다
  const minute = Math.floor(date.getTime() / 60000) * 60000;
  return Math.round((asUtc - minute) / 60000);
};

/** tz 의 벽시계 시각 → 실제 시각 */
export const zonedToDate = (tz, y, m, d, h, min) => {
  const guess = new Date(Date.UTC(y, m - 1, d, h, min));
  return new Date(guess.getTime() - tzOffsetMinutes(guess, tz) * 60000);
};

/** 화면 시간대 기준 연·월·일·시 */
export const zonedHourParts = (date, tz) => {
  const p = zonedParts(date, tz);
  return { y: p.y, m: p.m, d: p.d, h: p.h };
};

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const pad = (n) => String(n).padStart(2, '0');

/** 'HH:mm' (24시는 00:00) */
export const formatClock = (date, tz) => {
  const p = zonedParts(date, tz);
  return `${pad(p.h)}:${pad(p.min)}`;
};

/** 'Oct 16' */
export const formatDay = (date, tz) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
  }).format(date);

/** 'Oct 16, 18:00' */
export const formatDue = (iso, tz) => {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  return `${formatDay(date, tz)}, ${formatClock(date, tz)}`;
};

/** Intl 이 약어를 안 주는 시간대 (en 로케일은 Asia/Seoul 을 'GMT+9' 로 준다) */
const TZ_ABBR = {
  'Asia/Seoul': 'KST',
  'Asia/Tokyo': 'JST',
  'Asia/Ho_Chi_Minh': 'ICT',
  'Asia/Bangkok': 'ICT',
  'Asia/Jakarta': 'WIB',
  'Asia/Kolkata': 'IST',
};

/** 'KST' · 'CEST' 처럼 짧은 이름. 약어가 없으면 'GMT+6' */
export const tzShortName = (tz, at = new Date()) =>
  TZ_ABBR[tz] ??
  new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value ??
  tz;

/** 'UTC+9' */
export const tzOffsetLabel = (tz, at = new Date()) => {
  const off = tzOffsetMinutes(at, tz);
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? `:${pad(m)}` : ''}`;
};

/**
 * 작업 시간 안인가 (= online). 본인 시간대 기준으로 판정한다.
 * from > to 면 자정을 넘긴 구간 (예: 16:00–06:00).
 */
export const isWithinHours = (at, tz, from, to) => {
  const p = zonedParts(at, tz);
  const now = p.h * 60 + p.min;
  const a = toMinutes(from);
  const b = toMinutes(to);
  if (a === b) return true; // 하루 종일
  return a < b ? now >= a && now < b : now >= a || now < b;
};

/**
 * 본인 시간대의 작업 시간을 다른 시간대로 옮겨 'HH:mm–HH:mm' 로 보여준다.
 * 기준일은 at 이 속한 날 (서머타임이 바뀌는 날이 아니면 결과가 같다).
 */
export const formatHoursIn = (memberTz, from, to, displayTz, at = new Date()) => {
  const p = zonedParts(at, memberTz);
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  const start = zonedToDate(memberTz, p.y, p.m, p.d, fh, fm);
  const end = zonedToDate(memberTz, p.y, p.m, p.d, th, tm);
  return `${formatClock(start, displayTz)}–${formatClock(end, displayTz)}`;
};

/* ── 작업 시간 구간 (여러 개) ──────────────────────────── */

/** 작업 시간 구간은 최대 5개 (예: 10:00–12:00, 15:00–20:00) */
export const MAX_SLOTS = 5;

/** 사람의 작업 시간 구간 [{ from, to }] — 여러 구간(availableSlots), 없으면 예전 한 구간(availableFrom ~ To) */
export const slotsOf = (m) => {
  const list = (m?.availableSlots ?? []).filter((x) => x?.from && x?.to);
  if (list.length) return list;
  return m?.availableFrom && m?.availableTo ? [{ from: m.availableFrom, to: m.availableTo }] : [];
};

/** 지금 작업 시간 안인가 — 구간 중 하나라도 안이면 online. 작업 시간이 없으면 늘 online */
export const isMemberWorking = (at, m) => {
  const list = slotsOf(m);
  if (!list.length || !m?.timezone) return true;
  return list.some((x) => isWithinHours(at, m.timezone, x.from, x.to));
};

/** 작업 시간을 다른 시간대로 — '10:00–12:00, 15:00–20:00' */
export const formatMemberHours = (m, displayTz, at = new Date()) =>
  slotsOf(m)
    .map((x) => formatHoursIn(m.timezone, x.from, x.to, displayTz, at))
    .join(', ');

/** 구간끼리 겹치는가 (자정을 넘는 구간 포함, 하루 1,440분 위에서 본다) */
export const slotsOverlap = (list) => {
  const used = new Uint8Array(24 * 60);
  for (const x of list) {
    const a = toMinutes(x.from);
    const b = toMinutes(x.to);
    const len = a === b ? 24 * 60 : (b - a + 24 * 60) % (24 * 60);
    for (let i = 0; i < len; i += 1) {
      const k = (a + i) % (24 * 60);
      if (used[k]) return true;
      used[k] = 1;
    }
  }
  return false;
};

const SLOT_ERRORS = {
  empty: 'Add at least one time range.',
  same: 'Start and end can’t be the same.',
  order: 'End must be after start.',
  overlap: 'Time ranges can’t overlap.',
};

/** 구간 목록 검사 — 문제 없으면 null. messages 로 화면마다 문구를 바꾼다 */
export const slotsError = (slots, { overnight = true, messages = SLOT_ERRORS } = {}) => {
  if (!slots.length) return messages.empty;
  if (slots.some((x) => x.from === x.to)) return messages.same;
  if (!overnight && slots.some((x) => x.to < x.from)) return messages.order;
  if (slotsOverlap(slots)) return messages.overlap;
  return null;
};

/* ── 작업 시간만 세기 (offline 제외) ─────────────────────── */

const workWindows = (at, tz, from, to, days) => {
  const p = zonedParts(at, tz);
  const [fh, fm] = from.split(':').map(Number);
  const [th, tm] = to.split(':').map(Number);
  const allDay = toMinutes(from) === toMinutes(to);
  const overnight = toMinutes(from) > toMinutes(to);
  const out = [];
  for (let i = -1; i <= days; i += 1) {
    const day = new Date(Date.UTC(p.y, p.m - 1, p.d + i));
    const [y, m, d] = [day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()];
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    const [ny, nm, nd] = [next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate()];
    if (allDay) {
      out.push([zonedToDate(tz, y, m, d, 0, 0).getTime(), zonedToDate(tz, ny, nm, nd, 0, 0).getTime()]);
    } else {
      const s = zonedToDate(tz, y, m, d, fh, fm).getTime();
      const e = overnight
        ? zonedToDate(tz, ny, nm, nd, th, tm).getTime()
        : zonedToDate(tz, y, m, d, th, tm).getTime();
      out.push([s, e]);
    }
  }
  return out;
};

/** 사람의 모든 구간을 실제 시각 창으로 펼쳐 시간순으로 (겹치면 합친다) */
const memberWindows = (at, member, days) => {
  const list = slotsOf(member)
    .flatMap((x) => workWindows(at, member.timezone, x.from, x.to, days))
    .sort((p, q) => p[0] - q[0]);
  const out = [];
  list.forEach(([s, e]) => {
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  });
  return out;
};

/** 사람의 작업 시간 창 [시작ms, 끝ms][] — at 하루 전부터 days 일 뒤까지 (Dashboard 의 Team now 레인) */
export const workWindowsOf = (at, member, days) => memberWindows(new Date(at), member, days);

export const addWorkingMinutes = (start, minutes, member) => {
  const t0 = new Date(start).getTime();
  let left = minutes * 60000;
  for (const [s, e] of memberWindows(new Date(t0), member, 30)) {
    const from = Math.max(s, t0);
    if (e <= from) continue;
    if (e - from >= left) return new Date(from + left);
    left -= e - from;
  }
  return new Date(t0 + minutes * 60000);
};

export const workingMinutesBetween = (a, b, member) => {
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (tb <= ta) return 0;
  const days = Math.ceil((tb - ta) / 86_400_000) + 1;
  let sum = 0;
  memberWindows(new Date(ta), member, days).forEach(([s, e]) => {
    const lo = Math.max(s, ta);
    const hi = Math.min(e, tb);
    if (hi > lo) sum += hi - lo;
  });
  return Math.round(sum / 60000);
};

export const nextWorkStart = (at, member) => {
  const t = new Date(at).getTime();
  const windows = memberWindows(new Date(t), member, 3);
  const inside = windows.find(([s, e]) => s <= t && t < e);
  if (inside) return new Date(t);
  const next = windows.find(([s]) => s > t);
  return next ? new Date(next[0]) : new Date(t);
};

export const formatHm = (minutes) => {
  const m = Math.max(0, Math.round(minutes));
  return `${Math.floor(m / 60)}h ${pad(m % 60)}m`;
};

/** <input type="datetime-local"> 값 ('YYYY-MM-DDTHH:mm') — tz 의 벽시계 시각으로 쓴다 */
export const toInputValue = (date, tz) => {
  const p = zonedParts(date, tz);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}T${pad(p.h)}:${pad(p.min)}`;
};

/** datetime-local 값을 tz 의 벽시계 시각으로 읽어 실제 시각으로 */
export const fromInputValue = (value, tz) => {
  if (!value) return null;
  const [d, t] = value.split('T');
  const [y, m, day] = d.split('-').map(Number);
  const [h, min] = (t ?? '00:00').split(':').map(Number);
  return zonedToDate(tz, y, m, day, h, min);
};

/* ── 시간대 고르기 (신청 폼·Pre-join) ─────────────────────── */

/** 브라우저 시간대 — 기본값으로 쓴다 */
export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

/** Intl 이 아는 IANA 시간대 전부 */
export const allTimezones = () => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [browserTimezone()].filter(Boolean);
  }
};

/** "UTC+9" */
export const utcOffsetLabel = (tz, at = new Date()) => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value.replace('GMT', 'UTC') ?? '';
  } catch {
    return '';
  }
};

